import { useRef, useCallback } from 'react';
import { uploadPickedPhoto } from '../services/storage/photoUpload';

/**
 * useProfilePhotoPicker — one tap, one picker.
 *
 * Tapping the photo used to open the app's own "Select Profile Photo" sheet,
 * whose primary button then opened the iOS picker: two overlays to do one thing
 * (user, 2026-08-19). On production the tap now goes straight to the iOS picker.
 *
 * This hook only opens the OS picker. Whether a screen offers the app's
 * stock-photo sheet instead is the call site's decision, made with the same
 * inline `process.env.REACT_APP_BRANCH !== 'main'` gate the page already uses
 * for "Load default info" and "Clear data" — one rule, one place, not a second
 * copy buried in here.
 *
 * @param {function} onPhoto - called with the hosted URL once a photo is picked
 */
const useProfilePhotoPicker = (onPhoto) => {
  const inputRef = useRef(null);

  const openPhotoPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    // Clear the input so picking the SAME file again still fires a change event
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Compress + upload live in services/storage/photoUpload — one home for
    // "picked file → hosted URL", shared with the QR photo sheet.
    const photoUrl = await uploadPickedPhoto(file);
    if (!photoUrl) {
      alert('Error processing image. Please try a different photo.');
      return;
    }
    onPhoto?.(photoUrl);
  };

  const photoInput = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      onChange={handleFile}
      style={{ display: 'none' }}
    />
  );

  return { openPhotoPicker, photoInput };
};

export default useProfilePhotoPicker;
